import { canAccessModule } from "@/lib/permissions";
import { tierMeetsMinimum, TIER_DISPLAY, type SubscriptionTier } from "@/lib/tier-config";
import type { Capability, CapabilityResult, ResolveContext } from "./types";
import { CAPABILITIES } from "./registry";

export function resolveCapability(
  key: string,
  ctx: ResolveContext,
  registry: Record<string, Capability> = CAPABILITIES,
): CapabilityResult {
  const cap = registry[key];
  if (!cap) {
    return { available: false, blockedBy: null, reason: `Unknown capability "${key}"` };
  }

  // Precedence: parent → role → tier → toggle → flag
  if (cap.parent) {
    const parent = resolveCapability(cap.parent, ctx, registry);
    if (!parent.available) {
      return {
        available: false,
        blockedBy: "parent",
        reason: `${cap.label} is unavailable because ${registry[cap.parent]?.label ?? cap.parent} is off`,
      };
    }
  }

  if (cap.rbac && !canAccessModule(ctx.roles, cap.rbac)) {
    return { available: false, blockedBy: "role", reason: `You don't have permission to use ${cap.label}` };
  }

  const requiredTier: SubscriptionTier = cap.tier ?? "free";
  if (!tierMeetsMinimum(ctx.tier, requiredTier)) {
    const t = TIER_DISPLAY[requiredTier];
    return {
      available: false,
      blockedBy: "tier",
      requiredTier,
      reason: `${cap.label} requires the ${t.name} plan ($${t.monthlyPrice}/mo)`,
    };
  }

  if (cap.toggle && ctx.toggles[key] === false) {
    return {
      available: false,
      blockedBy: "toggle",
      reason: `${cap.label} has been disabled by your administrator. Enable it in Settings > Organization > Modules.`,
    };
  }

  if (cap.flagKey && ctx.isFlagKilled(cap.flagKey)) {
    return { available: false, blockedBy: "flag", reason: `${cap.label} is temporarily unavailable` };
  }

  return { available: true, blockedBy: null, reason: null };
}
